/**
 * UI Rendering Module
 * Contains the OverlaySystem for guidance highlighting, animated cursor, and tooltips
 */

import { SDK_WIDGET_ATTR, HybridTarget } from './types';
import { HybridTargetResolver } from './action-execution';
import { logger as defaultLogger, Logger } from './logger';

/**
 * UI Overlay System for guidance and highlighting
 */
export class OverlaySystem {
  private readonly logger: Logger;
  private container: HTMLDivElement | null = null;
  private highlightEl: HTMLDivElement | null = null;
  private tooltipElement: HTMLDivElement | null = null;
  private floeCursor: HTMLDivElement | null = null;
  private cursorHasBeenPositioned: boolean = false; // Track if cursor has been shown at least once

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
  }

  /**
   * Inject overlay into the page
   */
  inject() {
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.id = 'onboarding-overlay';
    this.container.setAttribute(SDK_WIDGET_ATTR, 'overlay');
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 2147483647;
    `;

    document.body.appendChild(this.container);

    // Inject the Floe cursor
    this.injectFloeCursor();

    this.logger.log('[OverlaySystem] Injected');
  }

  /**
   * Create and inject the Floe collaborative cursor - Premium Raycast-style design
   */
  private injectFloeCursor() {
    if (this.floeCursor) return;

    this.floeCursor = document.createElement('div');
    this.floeCursor.id = 'floe-cursor';
    this.floeCursor.setAttribute(SDK_WIDGET_ATTR, 'cursor');
    this.floeCursor.innerHTML = `
      <div class="floe-cursor-pointer">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="cursorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#ffffff"/>
              <stop offset="100%" style="stop-color:#f0f0f0"/>
            </linearGradient>
          </defs>
          <!-- Dark outline for visibility on light backgrounds -->
          <path d="M5.5 3L19.5 12L12 13.5L9 21L5.5 3Z" fill="none" stroke="rgba(0,0,0,0.5)" stroke-width="3.5" stroke-linejoin="round"/>
          <!-- Main cursor with white fill and dark inner stroke for contrast -->
          <path d="M5.5 3L19.5 12L12 13.5L9 21L5.5 3Z" fill="url(#cursorGradient)" stroke="#1a1a1a" stroke-width="1.2" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="floe-cursor-label">
        <span class="floe-cursor-name">Floe</span>
      </div>
    `;
    this.floeCursor.style.cssText = `
      position: fixed;
      top: -100px;
      left: -100px;
      pointer-events: none;
      z-index: 2147483646;
      display: flex;
      align-items: flex-start;
      gap: 4px;
      opacity: 0;
      transform: scale(0.8);
      transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
    `;

    // Add styles for cursor - Clean and minimal
    const style = document.createElement('style');
    style.id = 'floe-cursor-styles';
    style.textContent = `
      .floe-cursor-pointer {
        position: relative;
      }
      #floe-cursor svg {
        filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
      .floe-cursor-label {
        background: linear-gradient(135deg, #2a2a2f 0%, #1a1a1f 100%);
        color: white;
        font-size: 11px;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 6px;
        margin-top: 4px;
        box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4), 0 1px 3px rgba(0, 0, 0, 0.2);
        white-space: nowrap;
        letter-spacing: 0.02em;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
      }
      .floe-cursor-name {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      @keyframes floeCursorPulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      @keyframes floeCursorClick {
        0% { transform: scale(1); }
        30% { transform: scale(0.85); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(this.floeCursor);
    this.logger.log('[OverlaySystem] Floe cursor injected');
  }

  /**
   * Show the Floe cursor
   * On first appearance, positions the cursor at the bot floating icon (bottom-right)
   * so the initial animation starts from a natural origin point.
   */
  showFloeCursor() {
    if (this.floeCursor) {
      // On first appearance, set initial position to the bot's floating icon (bottom-right corner)
      if (!this.cursorHasBeenPositioned) {
        const iconX = window.innerWidth - 50;  // ControlHub is at right: 24px, orb ~52px wide
        const iconY = window.innerHeight - 50; // ControlHub is at bottom: 24px, orb ~52px tall
        this.floeCursor.style.left = `${iconX}px`;
        this.floeCursor.style.top = `${iconY}px`;
        this.cursorHasBeenPositioned = true;
        this.logger.log('[OverlaySystem] Cursor initial position set to bot icon:', iconX, iconY);
      }
      this.floeCursor.style.opacity = '1';
      this.floeCursor.style.transform = 'scale(1)';
    }
  }

  /**
   * Hide the Floe cursor
   */
  hideFloeCursor() {
    if (this.floeCursor) {
      this.floeCursor.style.opacity = '0';
      this.floeCursor.style.transform = 'scale(0.8)';
    }
  }

  /**
   * Animate the Floe cursor to a position
   */
  animateFloeCursorTo(x: number, y: number, duration: number = 400): Promise<void> {
    return new Promise((resolve) => {
      if (!this.floeCursor) {
        resolve();
        return;
      }

      // Show cursor if hidden
      this.showFloeCursor();

      // Get current position
      const currentTop = parseFloat(this.floeCursor.style.top) || 0;
      const currentLeft = parseFloat(this.floeCursor.style.left) || 0;

      // Calculate easing animation
      const startTime = performance.now();
      const startX = currentLeft;
      const startY = currentTop;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic for smooth deceleration
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        const newX = startX + (x - startX) * easeProgress;
        const newY = startY + (y - startY) * easeProgress;

        if (this.floeCursor) {
          this.floeCursor.style.left = `${newX}px`;
          this.floeCursor.style.top = `${newY}px`;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Animate cursor click effect
   */
  animateFloeCursorClick(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.floeCursor) {
        resolve();
        return;
      }

      const svg = this.floeCursor.querySelector('svg');

      if (svg) {
        // Animate the cursor pointer
        svg.style.animation = 'floeCursorClick 0.2s ease';

        setTimeout(() => {
          svg.style.animation = '';
          resolve();
        }, 200);
      } else {
        resolve();
      }
    });
  }

  /**
   * Move cursor to element and click
   */
  async moveToElementAndClick(element: HTMLElement): Promise<void> {
    const rect = element.getBoundingClientRect();
    const targetX = rect.left + rect.width / 2 - 5; // Offset for cursor tip
    const targetY = rect.top + rect.height / 2 - 5;

    // Animate cursor to element
    await this.animateFloeCursorTo(targetX, targetY, 500);

    // Small pause before click
    await new Promise(r => setTimeout(r, 100));

    // Click animation
    await this.animateFloeCursorClick();
  }

  /**
   * Show consent modal for screen sharing - Raycast style dark theme
   */
  showScreenShareModal(): Promise<boolean> {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.setAttribute(SDK_WIDGET_ATTR, 'consent-modal');
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(12px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2147483647;
        pointer-events: auto;
        animation: modalFadeIn 0.2s ease-out;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
      `;

      const content = document.createElement('div');
      content.style.cssText = `
        background: linear-gradient(180deg, rgba(32, 32, 36, 0.98) 0%, rgba(24, 24, 28, 0.99) 100%);
        border-radius: 20px;
        padding: 32px;
        max-width: 420px;
        width: 90%;
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), 0 24px 48px rgba(0, 0, 0, 0.5), 0 12px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
        animation: modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      `;

      content.innerHTML = `
        <style>
          @keyframes modalFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes modalSlideUp {
            from { transform: translateY(20px) scale(0.98); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes modalFadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        </style>
        
        <!-- Icon -->
        <div style="display: flex; justify-content: center; margin-bottom: 24px;">
          <div style="
            width: 64px;
            height: 64px;
            border-radius: 16px;
            background: linear-gradient(135deg, rgba(40, 40, 45, 0.9) 0%, rgba(30, 30, 35, 0.95) 100%);
            border: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
          ">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.8)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
              <circle cx="12" cy="10" r="2" fill="rgba(255, 255, 255, 0.8)" stroke="none"/>
            </svg>
          </div>
        </div>
        
        <!-- Title -->
        <h2 style="margin: 0 0 12px 0; font-size: 20px; font-weight: 600; color: rgba(255, 255, 255, 0.95); text-align: center; letter-spacing: -0.02em;">
          Enable Smart Guidance
        </h2>
        
        <!-- Description -->
        <p style="margin: 0 0 24px 0; color: rgba(255, 255, 255, 0.5); font-size: 14px; line-height: 1.6; text-align: center;">
          Share your screen so the AI can see what you're working on and provide contextual help.
        </p>
        
        <!-- Features -->
        <div style="background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 16px; margin-bottom: 24px; border: 1px solid rgba(255, 255, 255, 0.05);">
          <!-- Feature 1 -->
          <div style="display: flex; align-items: center; gap: 12px; padding-bottom: 12px; margin-bottom: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.05);">
            <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(96, 165, 250, 0.15); border: 1px solid rgba(96, 165, 250, 0.3); display: flex; align-items: center; justify-content: center;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 13px; font-weight: 600; color: rgba(255, 255, 255, 0.9); margin-bottom: 2px;">Visual Context</div>
              <div style="font-size: 12px; color: rgba(255, 255, 255, 0.4);">AI sees exactly what you see</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          
          <!-- Feature 2 -->
          <div style="display: flex; align-items: center; gap: 12px; padding-bottom: 12px; margin-bottom: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.05);">
            <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(52, 211, 153, 0.15); border: 1px solid rgba(52, 211, 153, 0.3); display: flex; align-items: center; justify-content: center;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 13px; font-weight: 600; color: rgba(255, 255, 255, 0.9); margin-bottom: 2px;">Faster Help</div>
              <div style="font-size: 12px; color: rgba(255, 255, 255, 0.4);">No need to explain what's on screen</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          
          <!-- Feature 3 -->
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="width: 32px; height: 32px; border-radius: 8px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.15); display: flex; align-items: center; justify-content: center;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.7)" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div style="flex: 1;">
              <div style="font-size: 13px; font-weight: 600; color: rgba(255, 255, 255, 0.9); margin-bottom: 2px;">Privacy First</div>
              <div style="font-size: 12px; color: rgba(255, 255, 255, 0.4);">Only this tab, stop anytime</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 255, 255, 0.5)" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
        </div>
        
        <!-- Primary Button -->
        <button type="button" id="screen-share-allow" style="
          width: 100%;
          padding: 14px 20px;
          background: rgba(255, 255, 255, 0.95);
          color: rgba(0, 0, 0, 0.9);
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        ">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          Enable Screen Share
        </button>
        
        <!-- Secondary Button -->
        <button type="button" id="screen-share-deny" style="
          width: 100%;
          margin-top: 12px;
          padding: 12px 20px;
          background: transparent;
          color: rgba(255, 255, 255, 0.4);
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        ">
          Maybe Later
        </button>
        
        <!-- Footer -->
        <p style="margin: 16px 0 0 0; font-size: 11px; color: rgba(255, 255, 255, 0.3); text-align: center;">
          You can enable this anytime from the control panel
        </p>
      `;

      modal.appendChild(content);
      document.body.appendChild(modal);

      // Add hover effects
      const allowBtn = content.querySelector('#screen-share-allow') as HTMLButtonElement;
      const denyBtn = content.querySelector('#screen-share-deny') as HTMLButtonElement;

      allowBtn.onmouseenter = () => {
        allowBtn.style.transform = 'translateY(-1px)';
        allowBtn.style.background = 'rgba(255, 255, 255, 1)';
        allowBtn.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
      };
      allowBtn.onmouseleave = () => {
        allowBtn.style.transform = 'translateY(0)';
        allowBtn.style.background = 'rgba(255, 255, 255, 0.95)';
        allowBtn.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
      };

      denyBtn.onmouseenter = () => {
        denyBtn.style.color = 'rgba(255, 255, 255, 0.8)';
      };
      denyBtn.onmouseleave = () => {
        denyBtn.style.color = 'rgba(255, 255, 255, 0.4)';
      };

      // Handle clicks
      allowBtn.onclick = () => {
        modal.style.animation = 'modalFadeOut 0.2s ease';
        setTimeout(() => {
          modal.remove();
          resolve(true);
        }, 200);
      };

      denyBtn.onclick = () => {
        modal.style.animation = 'modalFadeOut 0.2s ease';
        setTimeout(() => {
          modal.remove();
          resolve(false);
        }, 200);
      };
    });
  }

  /**
   * Highlight an element using CSS selector (legacy method)
   */
  highlight(selector: string, options: { color?: string; padding?: number } = {}) {
    const element = document.querySelector(selector);
    if (!element) {
      this.logger.warn('[OverlaySystem] Element not found:', selector);
      return;
    }

    this.highlightElement(element as HTMLElement, options);
  }

  /**
   * Highlight an element using hybrid target (new method)
   * Uses multi-strategy resolution to find the element
   */
  highlightTarget(target: HybridTarget, options: { color?: string; padding?: number } = {}): boolean {
    this.logger.log('╔══════════════════════════════════════════════════════════════╗');
    this.logger.log('║  [OverlaySystem] 💡 HIGHLIGHT TARGET CALLED                  ║');
    this.logger.log('╚══════════════════════════════════════════════════════════════╝');
    this.logger.log('[OverlaySystem] Target:', JSON.stringify(target, null, 2));
    this.logger.log('[OverlaySystem] Options:', options);

    const element = HybridTargetResolver.resolveTarget(target);
    if (!element) {
      this.logger.warn('[OverlaySystem] ❌ Element not found for target');
      return false;
    }

    this.logger.log('[OverlaySystem] ✅ Element found, applying highlight...');
    this.logger.log('[OverlaySystem] Element details:');
    this.logger.log('  • tagName:', element.tagName);
    this.logger.log('  • id:', element.id || '(none)');
    this.logger.log('  • classes:', element.className || '(none)');
    this.logger.log('  • innerText:', (element.innerText || '').substring(0, 50) + '...');

    this.highlightElement(element, options);
    this.logger.log('[OverlaySystem] 🎨 Highlight applied successfully!');
    return true;
  }

  /**
   * Highlight an already-resolved HTMLElement directly
   * Use this when you already have the element to avoid re-resolution
   */
  highlightResolvedElement(element: HTMLElement, options: { color?: string; padding?: number } = {}) {
    return this.highlightElement(element, options);
  }

  /**
   * Internal method to highlight an HTMLElement - Clean, non-intrusive design
   * Uses dual-layer border for visibility on BOTH light and dark backgrounds
   */
  private highlightElement(element: HTMLElement, options: { color?: string; padding?: number } = {}) {
    // Default to Floe purple - works on all backgrounds with the dual-layer approach
    const { color = 'rgba(124, 58, 237, 0.9)', padding = 4 } = options;
    this.logger.log('[OverlaySystem] highlightElement() called');
    this.logger.log('[OverlaySystem] Highlight color:', color);
    this.logger.log('[OverlaySystem] Highlight padding:', padding);

    // Extract RGB values from the color for consistent theming in shadows
    // Supports: rgb/rgba, hex (#RRGGBB/#RGB), and CSS variables
    let r = 124, g = 58, b = 237; // Default Floe purple
    
    let resolvedColor = color;
    
    // Resolve CSS variables first
    if (color.startsWith('var(')) {
      const varMatch = color.match(/var\(\s*(--[^,)]+)/);
      if (varMatch) {
        const varValue = getComputedStyle(document.documentElement).getPropertyValue(varMatch[1]).trim();
        if (varValue) resolvedColor = varValue;
      }
    }
    
    // Parse hex format (#RRGGBB or #RGB)
    const hexMatch = resolvedColor.match(/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/);
    if (hexMatch) {
      const hex = hexMatch[1];
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        // #RGB shorthand
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      }
    } else {
      // Parse rgb/rgba format
      const rgbMatch = resolvedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1]);
        g = parseInt(rgbMatch[2]);
        b = parseInt(rgbMatch[3]);
      }
    }

    if (!this.highlightEl) {
      this.logger.log('[OverlaySystem] Creating new highlight element');
      this.highlightEl = document.createElement('div');
      this.highlightEl.id = 'hse-highlight-element';
      this.highlightEl.setAttribute(SDK_WIDGET_ATTR, 'highlight');
      this.container?.appendChild(this.highlightEl);
    }

    // Scroll element into view first
    this.logger.log('[OverlaySystem] Scrolling element into view...');
    HybridTargetResolver.scrollIntoView(element);

    // Wait a bit for scroll to complete before positioning highlight
    this.logger.log('[OverlaySystem] Waiting 300ms for scroll to complete...');
    setTimeout(() => {
      const updatedRect = element.getBoundingClientRect();
      this.logger.log('[OverlaySystem] Element position after scroll:');
      this.logger.log('  • top:', updatedRect.top);
      this.logger.log('  • left:', updatedRect.left);
      this.logger.log('  • width:', updatedRect.width);
      this.logger.log('  • height:', updatedRect.height);

      if (this.highlightEl) {
        // Clean highlight with dual-layer border for visibility on ALL backgrounds
        // Layer 1: Dark outline (visible on light backgrounds)
        // Layer 2: Colored glow (brand color)
        // Layer 3: Light outline (visible on dark backgrounds)
        // Layer 4: Colored ambient glow
        // Layer 5: Inner light border (adds depth)
        this.highlightEl.style.cssText = `
          position: fixed;
          top: ${updatedRect.top - padding}px;
          left: ${updatedRect.left - padding}px;
          width: ${updatedRect.width + padding * 2}px;
          height: ${updatedRect.height + padding * 2}px;
          pointer-events: none;
          z-index: 2147483645;
          border: 2.5px solid rgba(${r}, ${g}, ${b}, 0.9);
          border-radius: 8px;
          background: transparent;
          box-shadow: 
            0 0 0 1.5px rgba(0, 0, 0, 0.3),
            0 0 0 4px rgba(${r}, ${g}, ${b}, 0.25),
            0 0 0 5.5px rgba(255, 255, 255, 0.5),
            0 0 20px rgba(${r}, ${g}, ${b}, 0.4),
            inset 0 0 0 1px rgba(255, 255, 255, 0.15);
          animation: highlightFadeIn 0.2s ease-out, highlightPulse 2s ease-in-out infinite;
        `;
        this.logger.log('[OverlaySystem] ✨ HIGHLIGHT VISIBLE! Position:', {
          top: updatedRect.top - padding,
          left: updatedRect.left - padding,
          width: updatedRect.width + padding * 2,
          height: updatedRect.height + padding * 2
        });
      }
    }, 300);

    // Add highlight animations
    if (!document.querySelector('#onboarding-animations')) {
      const style = document.createElement('style');
      style.id = 'onboarding-animations';
      style.textContent = `
        @keyframes highlightFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes highlightPulse {
          0%, 100% { 
            border-color: rgba(124, 58, 237, 0.85);
            box-shadow: 
              0 0 0 1.5px rgba(0, 0, 0, 0.25),
              0 0 0 4px rgba(124, 58, 237, 0.2),
              0 0 0 5.5px rgba(255, 255, 255, 0.4),
              0 0 20px rgba(124, 58, 237, 0.35),
              inset 0 0 0 1px rgba(255, 255, 255, 0.1);
          }
          50% { 
            border-color: rgba(124, 58, 237, 1);
            box-shadow: 
              0 0 0 2px rgba(0, 0, 0, 0.3),
              0 0 0 5px rgba(124, 58, 237, 0.35),
              0 0 0 7px rgba(255, 255, 255, 0.5),
              0 0 30px rgba(124, 58, 237, 0.5),
              inset 0 0 0 1px rgba(255, 255, 255, 0.2);
          }
        }
        
        @keyframes strongPulse {
          0%, 100% { 
            border-color: rgba(124, 58, 237, 0.9);
            box-shadow: 
              0 0 0 2px rgba(0, 0, 0, 0.3),
              0 0 0 5px rgba(124, 58, 237, 0.25),
              0 0 0 7px rgba(255, 255, 255, 0.5),
              0 0 25px rgba(124, 58, 237, 0.4),
              inset 0 0 0 1px rgba(255, 255, 255, 0.15);
          }
          50% { 
            border-color: rgba(124, 58, 237, 1);
            box-shadow: 
              0 0 0 2.5px rgba(0, 0, 0, 0.35),
              0 0 0 6px rgba(124, 58, 237, 0.4),
              0 0 0 8.5px rgba(255, 255, 255, 0.6),
              0 0 40px rgba(124, 58, 237, 0.55),
              inset 0 0 0 1.5px rgba(255, 255, 255, 0.25);
          }
        }
        
        #hse-highlight-element.strong-pulse {
          border-width: 3px;
          animation: highlightFadeIn 0.2s ease-out, strongPulse 1s ease-in-out infinite !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Pulse an element (similar to highlight but with stronger animation)
   */
  pulse(selector: string, options: { color?: string; padding?: number } = {}) {
    // Use highlight but with stronger pulse animation - default to white
    this.highlight(selector, { ...options, color: options.color || 'rgba(255, 255, 255, 0.8)' });

    // Enhance the pulse animation by adding strong-pulse class
    if (this.highlightEl) {
      this.highlightEl.classList.add('strong-pulse');
    }
  }

  /**
   * Pulse an element using hybrid target
   */
  pulseTarget(target: HybridTarget, options: { color?: string; padding?: number } = {}): boolean {
    const success = this.highlightTarget(target, { ...options, color: options.color || 'rgba(167, 139, 250, 0.8)' });

    // Enhance the pulse animation by adding strong-pulse class
    if (success && this.highlightEl) {
      this.highlightEl.classList.add('strong-pulse');
    }

    return success;
  }

  /**
   * Clear highlight
   */
  clearHighlight() {
    if (this.highlightEl) {
      this.highlightEl.remove();
      this.highlightEl = null;
    }
  }

  /**
   * Show tooltip
   */
  showTooltip(text: string, options: { x?: number; y?: number; anchor?: string } = {}) {
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('div');
      this.container?.appendChild(this.tooltipElement);
    }

    let x = options.x || window.innerWidth / 2;
    let y = options.y || window.innerHeight - 100;

    if (options.anchor) {
      const element = document.querySelector(options.anchor);
      if (element) {
        const rect = element.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.bottom + 10;
      }
    }

    this.tooltipElement.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      pointer-events: none;
      max-width: 300px;
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    this.tooltipElement.textContent = text;
  }

  /**
   * Hide tooltip
   */
  hideTooltip() {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  /**
   * Clean up all overlays: hide cursor, clear highlight, hide tooltip.
   * Call this on disconnect/errors/navigation to remove all visual guidance from screen.
   * Does NOT destroy DOM elements (unlike remove()), so overlay can be reused.
   */
  cleanup() {
    this.clearHighlight();
    this.hideFloeCursor();
    this.hideTooltip();
    this.logger.log('[OverlaySystem] Cleaned up all overlays');
  }

  /**
   * Reset cursor tracking state so next show starts from the bot icon again.
   * Call this on full disconnect to ensure fresh start on reconnect.
   */
  resetCursorState() {
    this.cursorHasBeenPositioned = false;
    if (this.floeCursor) {
      this.floeCursor.style.top = '-100px';
      this.floeCursor.style.left = '-100px';
      this.floeCursor.style.opacity = '0';
      this.floeCursor.style.transform = 'scale(0.8)';
    }
    this.logger.log('[OverlaySystem] Cursor state reset');
  }

  /**
   * Remove overlay and all injected style elements
   */
  remove() {
    // Remove injected style tags from document.head before nulling DOM refs
    const cursorStyles = document.getElementById('floe-cursor-styles');
    if (cursorStyles) {
      cursorStyles.remove();
    }
    const animationStyles = document.getElementById('onboarding-animations');
    if (animationStyles) {
      animationStyles.remove();
    }

    // Remove the Floe cursor from DOM
    if (this.floeCursor) {
      this.floeCursor.remove();
      this.floeCursor = null;
    }

    this.cursorHasBeenPositioned = false;
    this.container?.remove();
    this.container = null;
    this.highlightEl = null;
    this.tooltipElement = null;
  }
}
